import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { MermaidDiagram } from '@internal/backstage-plugin-knowledge';
import { codeBuilderApiRef } from '../../api';
import type {
  CodeImplementationDraft,
  CodeInterviewMessage,
  CodeIssueContext,
} from '../../api';
import type { CodeBuilderDraft, CodeBuilderFormData } from './types';

export const CodeBuilderInterviewPanel = (props: {
  formData: CodeBuilderFormData;
  onChange(patch: Partial<CodeBuilderFormData>): void;
}) => {
  const { formData, onChange } = props;
  const codeBuilderApi = useApi(codeBuilderApiRef);

  const repoEntityRef = formData.repoEntityRef;
  const issues = useMemo(() => formData.issues ?? [], [formData.issues]);

  const [messages, setMessages] = useState<CodeInterviewMessage[]>(
    () => formData.transcript ?? [],
  );
  const [requirementsSummary, setRequirementsSummary] = useState(
    formData.requirementsSummary ?? '',
  );
  const [draft, setDraft] = useState<CodeBuilderDraft | undefined>(
    formData.draft,
  );
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();

  const issueBodies = useRef(new Map<number, string>());
  const abortRef = useRef<AbortController>();

  const publish = useCallback(
    (
      nextMessages: CodeInterviewMessage[],
      nextSummary: string,
      nextDraft: CodeBuilderDraft | undefined,
      reviewConfirmed = formData.reviewConfirmed,
    ) => {
      const operations = nextDraft?.operations ?? [];
      const openQuestions = nextDraft?.openQuestions ?? [];
      onChange({
        transcript: nextMessages,
        requirementsSummary: nextSummary || undefined,
        draft: nextDraft,
        reviewConfirmed,
        review: {
          status: reviewStatus(Boolean(reviewConfirmed), operations.length),
          files: operations.map(operation => operation.path).join('\n'),
          openQuestions: openQuestions.length,
        },
      });
    },
    [formData.reviewConfirmed, onChange],
  );

  const send = useCallback(
    async (content: string) => {
      if (!repoEntityRef || !issues.length || !content.trim() || sending) {
        return;
      }
      setSending(true);
      setError(undefined);

      const userMessage: CodeInterviewMessage = { role: 'user', content };
      const withUser = [...messages, userMessage];
      setMessages(withUser);

      try {
        const issueContexts: CodeIssueContext[] = [];
        for (const issue of issues) {
          let body = issueBodies.current.get(issue.number);
          if (body === undefined) {
            try {
              const detail = await codeBuilderApi.getIssue(
                repoEntityRef,
                issue.number,
              );
              body = detail.issue.body;
              issueBodies.current.set(issue.number, body);
            } catch {
              body = '';
            }
          }
          issueContexts.push({
            number: issue.number,
            title: issue.title,
            url: issue.url,
            body,
          });
        }

        const controller = new AbortController();
        abortRef.current = controller;
        const response = await codeBuilderApi.sendInterviewMessage(
          {
            repoEntityRef,
            issues: issueContexts,
            messages: withUser,
          },
          controller.signal,
        );

        const withAssistant = [...withUser, response.message];
        setMessages(withAssistant);

        const nextDraft = draftFromResponse(response.draft);
        const nextSummary =
          response.draft?.requirementsSummary ?? requirementsSummary;
        if (nextDraft) {
          setDraft(nextDraft);
        }
        setRequirementsSummary(nextSummary);
        publish(withAssistant, nextSummary, nextDraft ?? draft);
      } catch (e) {
        if (!abortRef.current?.signal.aborted) {
          setError(e instanceof Error ? e.message : String(e));
        }
        setMessages(messages);
      } finally {
        setSending(false);
        abortRef.current = undefined;
      }
    },
    [
      codeBuilderApi,
      draft,
      issues,
      messages,
      publish,
      repoEntityRef,
      requirementsSummary,
      sending,
    ],
  );

  const startInterview = () =>
    void send('Start the interview for the selected issues.');

  const onToggleReviewed = (checked: boolean) => {
    onChange({ reviewConfirmed: checked });
    publish(messages, requirementsSummary, draft, checked);
  };

  if (!repoEntityRef || !issues.length) {
    return (
      <PanelMessage>
        Pick a repository and at least one issue above to start the interview.
      </PanelMessage>
    );
  }

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {messages.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                padding: '28px 8px',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {issues.length} issue{issues.length === 1 ? '' : 's'} selected.
                Start the interview when you're ready.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={startInterview}
                disabled={sending}
              >
                {sending ? 'Starting…' : 'Start interview'}
              </Button>
            </div>
          ) : null}
          {messages
            .filter(message => message.role !== 'system')
            .map((message, index) => (
              <ChatBubble key={index} message={message} />
            ))}
          {sending && messages.length > 0 ? (
            <div style={{ padding: '6px 0' }}>
              <CircularProgress size={14} />
            </div>
          ) : null}
          {error ? (
            <Alert severity="error" style={{ marginTop: 8 }}>
              {error}
            </Alert>
          ) : null}
        </div>
        <Divider />
        <form
          onSubmit={event => {
            event.preventDefault();
            const content = input.trim();
            if (content) {
              setInput('');
              void send(content);
            }
          }}
          style={{ display: 'flex', gap: 8, padding: 12 }}
        >
          <TextField
            value={input}
            onChange={event => setInput(event.target.value)}
            placeholder="Answer the assistant, or ask for a draft…"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            maxRows={4}
            disabled={sending}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={sending || !input.trim()}
          >
            Send
          </Button>
        </form>
      </div>

      <div
        style={{
          width: 380,
          flexShrink: 0,
          borderLeft: '1px solid rgba(128,128,128,0.3)',
          overflow: 'auto',
          padding: 12,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          C4 — Code level (L4)
        </Typography>
        {draft?.c4Diagram ? (
          <div style={{ marginBottom: 12, overflowX: 'auto', maxWidth: '100%' }}>
            <MermaidDiagram code={draft.c4Diagram} />
          </div>
        ) : (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginBottom: 12 }}
          >
            A C4 Code-level view of the affected component appears here.
          </Typography>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Class diagram
        </Typography>
        {draft?.classDiagram ? (
          <div style={{ marginBottom: 12, overflowX: 'auto', maxWidth: '100%' }}>
            <MermaidDiagram code={draft.classDiagram} />
          </div>
        ) : (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginBottom: 12 }}
          >
            A UML class diagram of the classes to add or change appears here.
          </Typography>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Sequence diagram
        </Typography>
        {draft?.sequenceDiagram ? (
          <div style={{ marginBottom: 12, overflowX: 'auto', maxWidth: '100%' }}>
            <MermaidDiagram code={draft.sequenceDiagram} />
          </div>
        ) : (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginBottom: 12 }}
          >
            A UML sequence diagram of the main flow appears here.
          </Typography>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Requirements
        </Typography>
        {requirementsSummary ? (
          <Typography
            variant="body2"
            style={{ whiteSpace: 'pre-wrap', marginBottom: 10 }}
          >
            {requirementsSummary}
          </Typography>
        ) : (
          <Typography variant="caption" color="textSecondary">
            The requirements summary appears here as the interview progresses.
          </Typography>
        )}

        {draft?.operations.length ? (
          <>
            <Typography variant="caption" color="textSecondary">
              Proposed file changes
            </Typography>
            <ul style={{ paddingLeft: 18, margin: '4px 0 10px' }}>
              {draft.operations.map(operation => (
                <li key={operation.path} style={{ marginBottom: 6 }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {operation.op} {operation.path}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {operation.explanation}
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {draft?.openQuestions.length ? (
          <>
            <Typography variant="caption" color="textSecondary">
              Open questions
            </Typography>
            <ul style={{ paddingLeft: 18, margin: '4px 0 10px' }}>
              {draft.openQuestions.map(question => (
                <li key={question}>
                  <Typography variant="body2">{question}</Typography>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {draft?.warnings.length ? (
          <Alert severity="warning" style={{ marginBottom: 10 }}>
            {draft.warnings.join('\n')}
          </Alert>
        ) : null}

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(formData.reviewConfirmed)}
              disabled={!draft?.operations.length}
              onChange={event => onToggleReviewed(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">Reviewed and confirmed</Typography>
          }
        />
      </div>
    </div>
  );
};

function reviewStatus(reviewConfirmed: boolean, operationCount: number): string {
  if (reviewConfirmed) {
    return 'Reviewed and confirmed';
  }
  return operationCount ? 'Draft ready for review' : 'Interview in progress';
}

function draftFromResponse(
  draft: CodeImplementationDraft | undefined,
): CodeBuilderDraft | undefined {
  if (!draft) {
    return undefined;
  }
  return {
    c4Diagram: draft.c4Diagram || undefined,
    classDiagram: draft.classDiagram || undefined,
    sequenceDiagram: draft.sequenceDiagram || undefined,
    operations: draft.operations,
    warnings: draft.warnings,
    openQuestions: draft.openQuestions,
  };
}

function ChatBubble(props: { message: CodeInterviewMessage }) {
  const isUser = props.message.role === 'user';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        margin: '6px 0',
      }}
    >
      <div
        style={{
          maxWidth: '80%',
          padding: '8px 10px',
          borderRadius: 10,
          background: isUser
            ? 'rgba(33,150,243,0.15)'
            : 'rgba(128,128,128,0.12)',
          whiteSpace: 'pre-wrap',
        }}
      >
        <Typography variant="body2">{props.message.content}</Typography>
      </div>
    </div>
  );
}

function PanelMessage(props: { children: ReactNode }) {
  return (
    <div style={{ padding: 16 }}>
      <Typography variant="body2" color="textSecondary">
        {props.children}
      </Typography>
    </div>
  );
}
